import { useEffect } from 'react';
import type { DependencyList } from 'react';
import { REPORT_IT_WEBSITE_LINK } from '@constants';

type PreventConfirmationReturnOptions = {
  confirmationUrl: string;
  redirectUrl?: string;
  onReset?: () => void;
  historyPushCount?: number;
  dependencies?: DependencyList;
};

const DEFAULT_HISTORY_PUSH_COUNT = 10;

export const usePreventConfirmationReturn = ({
  confirmationUrl,
  redirectUrl = REPORT_IT_WEBSITE_LINK,
  onReset,
  historyPushCount = DEFAULT_HISTORY_PUSH_COUNT,
  dependencies = [],
}: PreventConfirmationReturnOptions) => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const navigationEntries = window.performance?.getEntriesByType?.(
      'navigation',
    ) as PerformanceNavigationTiming[] | undefined;
    const navigationEntry = navigationEntries?.[0];

    if (navigationEntry?.type === 'back_forward') {
      window.location.replace(redirectUrl);
    }
  }, [redirectUrl]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    for (let i = 0; i < historyPushCount; i++) {
      window.history.pushState({ page: i }, '', confirmationUrl);
    }

    const handlePopState = () => {
      window.location.replace(redirectUrl);
      onReset?.();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [
    confirmationUrl,
    redirectUrl,
    onReset,
    historyPushCount,
    ...dependencies,
  ]);
};
